import { useEffect, useMemo, useState } from "react"
import AttendanceRecordToolbar, {
  FilterValueAttendantRecord,
} from "../../components/AttendantRecordToolBar"
import {
  IOvertimeRecordsEntity,
  IOvertimeRes,
  IShiftSwapsRes,
  ITimeSheetDocsEntity,
  ITimeSheetRes,
  TimeSheetReview,
} from "../../typings"
import { useGet } from "utils/useFetch"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Button, Form, Input, Pagination, Select } from "antd"
import downIcon from "assets/svg/chevron-down.svg"
import filterIcon from "assets/svg/filter.svg"
import sortIcon from "assets/svg/arrow-up-down.svg"
import warningIcon from "assets/svg/danger-circle.svg"
import { ewaAxiosInstance } from "utils/request"
import { onQueryError } from "utils/utils"
import TimesheetSkeleton from "./TimesheetSkeleton"
import TimeSheetTable from "../../components/TimeSheetTable"
import TimeSheetActivityRow from "../../components/TimeSheetActivityRow"
import { formatDateReadable } from "utils/formatDate"
import OvertimeTimeSheetActivityRow from "../../components/OvertimeTimeSheetActivityRow"
import FilterDrawer from "../../components/FilterDrawer"
import TimesheetDetailDrawer from "../../components/TimesheetDetailDrawer"
import OvertimeWarningBanner from "./OvertimeWarningBanner"
import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import { Button as CustomButton } from "components"
import ShiftSwapBanner from "./ShiftSwapBanner"
import {
  toggleIds,
  groupByDepartmentNoOvertime,
  groupByDepartmentWithOvertime,
  toDateGroups,
  buildFlags,
  OVERTIME_HEADERS,
  TIMESHEET_HEADERS,
  todaysDate,
  formatTime as formatTimeReadable,
} from "../utils/timesheet_utils"
import CustomEmptyState from "./CustomEmptyState"
import { useAuthStore } from "stores"
import { toast } from "react-toastify"
import { formatTime } from "pages/employee-dashboard/time-and-attendance/utils"

// ─── Types ────────────────────────────────────────────────────────────────────

type SelectedRowState = {
  selectedTimesheetRow: number[]
  selectedShiftRow: number[]
}

type ModalState = {
  approve: boolean
  reject: boolean
  rejectDetails: boolean
  reason: boolean
  overtime: boolean
  rejectOvertime: boolean
  openFilter: boolean
  isTurned: boolean
  viewUserProfileDrawer: boolean
}

export type TimesheetGroupBy = "day" | "week" | "month"

export interface GroupedDepartment {
  department: { id: number | string; name: string }
  employees: ITimeSheetDocsEntity[]
}

export interface GroupedOvertimeDepartment {
  department: { id: number; name: string }
  employees: IOvertimeRecordsEntity[]
}

// ─── Constants ────────────────────────────────────────────────────────────────

const EMPTY_IMAGE =
  "https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/52d2cc1e-6777-43ce-8838-393f18a2f4c1.png"

const INITIAL_MODAL_STATE: ModalState = {
  approve: false,
  reject: false,
  rejectDetails: false,
  reason: false,
  overtime: false,
  rejectOvertime: false,
  openFilter: false,
  isTurned: false,
  viewUserProfileDrawer: false,
}

const INITIAL_PAGE = { currentPage: 1, pageSize: 10 }

const buildTimesheetParams = (
  filterQuery: string,
  companyId: string,
  workplaceId: string,
  dutyLocationId: number | null,
  shiftPlanId?: string,
  shiftPeriodId?: string
): string => {
  const params = new URLSearchParams(filterQuery)
  if (companyId) params.set("companyId", companyId)
  if (workplaceId) params.set("workplaceId", workplaceId)
  if (dutyLocationId) params.set("dutyLocationId", String(dutyLocationId))
  else params.delete("dutyLocationId")
  if (shiftPlanId) params.set("shiftPlanId", shiftPlanId)
  if (shiftPeriodId) params.set("shiftPeriodId", shiftPeriodId)
  return params.toString()
}
// ─── Main Component ───────────────────────────────────────────────────────────

const TimesheetContent = ({ onClick }: { onClick: () => void }) => {
  const queryClient = useQueryClient()
  const [filterForm] = Form.useForm()
  const [rejectForm] = Form.useForm()
  const [overtimeRejectForm] = Form.useForm()
  const { x } = useParams()
  const navigate = useNavigate()

  const selectedFilterDpt = Form.useWatch("departments", filterForm)
  const departmentId = selectedFilterDpt?.[0]
  const { user } = useAuthStore((state) => state)
  const [searchParams, setSearchParams] = useSearchParams()
  const dutyLocationIdRaw = searchParams.get("dutyLocationId")
  const dutyLocationId = dutyLocationIdRaw ? Number(dutyLocationIdRaw) : null
  const companyId = user?.userCompanyId

  useEffect(() => {
    if (!companyId) return
    if (searchParams.get("companyId") === String(companyId)) return
    const next = new URLSearchParams(searchParams)
    next.set("companyId", String(companyId))
    setSearchParams(next, { replace: true })
  }, [companyId, searchParams, setSearchParams])

  const [tableConfig, setTableConfig] = useState({
    selectedFilter: "all",
    sort: "new",
    filterQuery: "",
    activeTab: "1",
  })

  const [page, setPage] = useState(INITIAL_PAGE)
  const [groupBy, setGroupBy] = useState<TimesheetGroupBy>("week")

  const [activeCollapseKeys, setActiveCollapseKeys] = useState<
    string | string[]
  >()

  const [selectedRow, setSelectedRow] = useState<SelectedRowState>({
    selectedTimesheetRow: [],
    selectedShiftRow: [],
  })

  const [modal, setModal] = useState<ModalState>(INITIAL_MODAL_STATE)

  const [selectedTimesheet, setSelectedTimesheet] = useState<
    ITimeSheetDocsEntity | IOvertimeRecordsEntity | undefined
  >(undefined)

  // ─── Modal helpers ────────────────────────────────────────────────────────

  const openModal = (
    key: keyof ModalState,
    entry?: typeof selectedTimesheet
  ) => {
    if (entry) setSelectedTimesheet(entry)
    setModal((prev) => ({ ...prev, [key]: true }))
  }

  const closeModal = (...keys: (keyof ModalState)[]) => {
    setModal((prev) => {
      const next = { ...prev }
      keys.forEach((k) => (next[k] = false as any))
      return next
    })
  }

  // ─── Data fetching ────────────────────────────────────────────────────────

  const { isLoading: isShiftSwapDataLoading, data: shiftSwapData } =
    useGet<IShiftSwapsRes>({
      url: `/v1/time-attendance/shift-swaps?workplaceId=${x}&companyId=${
        user?.userCompanyId
      }${dutyLocationId ? `&dutyLocationId=${dutyLocationId}` : ""}&page=${
        page.currentPage
      }&limit=100`,
      instanceKey: "ewaAxiosInstance",
    })

  const { isLoading: isLoadingTimesheet, data: timesheet } =
    useGet<ITimeSheetRes>({
      url: `/v1/time-attendance/timesheets?${buildTimesheetParams(
        tableConfig?.filterQuery ?? "",
        String(user?.userCompanyId ?? ""),
        x ?? "",
        dutyLocationId
      )}&page=${page.currentPage}&limit=${page.pageSize}&groupBy=${groupBy}`,
      instanceKey: "ewaAxiosInstance",
    })

  // The backend sends rows bucketed by date; this is every row in one list.
  const timesheetRows = Object.values(timesheet?.data?.docs ?? {}).flat()

  const { isLoading: isLoadingOvertime, data: overtime } = useGet<IOvertimeRes>(
    {
      url: `/v1/time-attendance/overtime?${buildTimesheetParams(
        tableConfig?.filterQuery ?? "",
        String(user?.userCompanyId ?? ""),
        x ?? "",
        dutyLocationId
      )}&page=${page.currentPage}&limit=${page.pageSize}`,
      instanceKey: "ewaAxiosInstance",
    }
  )

  // Re-sync the open drawer's selected entry whenever the underlying list
  // refetches (e.g. after an approve/reject mutation invalidates the query).
  // Without this the drawer would keep showing the stale snapshot taken on
  // click and the actions would still appear after approval.
  useEffect(() => {
    if (!selectedTimesheet?.id) return
    const next = timesheetRows.find((d) => d.id === selectedTimesheet.id)
    if (next && next !== selectedTimesheet) setSelectedTimesheet(next)
  }, [timesheet?.data?.docs, selectedTimesheet?.id])

  // ─── Query invalidation ───────────────────────────────────────────────────

  const invalidateTimesheetAndOvertime = () => {
    const params = buildTimesheetParams(
      tableConfig?.filterQuery ?? "",
      user?.userCompanyId?.toString() ?? "",
      x ?? "",
      dutyLocationId
    )

    const timesheetKey = `/v1/time-attendance/timesheets?${params}&page=${page.currentPage}&limit=${page.pageSize}&groupBy=${groupBy}`
    const overtimeKey = `/v1/time-attendance/overtime?${params}&page=${page.currentPage}&limit=${page.pageSize}`

    queryClient.invalidateQueries({ queryKey: [timesheetKey] })
    queryClient.invalidateQueries({ queryKey: [overtimeKey] })
  }

  const {
    isLoading: isViewTimesheetDetailsLoading,
    data: viewTimesheetDetails,
  } = useGet<TimeSheetReview>({
    url: `/v1/time-attendance/timesheets/${selectedTimesheet?.id}/reviews`,
    instanceKey: "ewaAxiosInstance",
    enabled: !!selectedTimesheet?.id,
  })

  // ─── Mutations ────────────────────────────────────────────────────────────

  // Untick the rows that were just approved or rejected.
  const unselectTimesheets = (timesheetIds: number[]) =>
    setSelectedRow((prev) => ({
      ...prev,
      selectedTimesheetRow: prev.selectedTimesheetRow.filter(
        (id) => !timesheetIds.includes(id)
      ),
    }))

  const {
    mutate: approveTimeSheet,
    isLoading: isApproving,
    variables: approveVariables,
  } = useMutation({
    mutationFn: ({ timesheetIds }: { timesheetIds: number[] }) =>
      ewaAxiosInstance.post(`/v1/time-attendance/timesheets/approve`, {
        timesheetIds,
      }),
    onSuccess: (_, { timesheetIds }) => {
      invalidateTimesheetAndOvertime()
      unselectTimesheets(timesheetIds)
      toast.success("Timesheet approved successfully")
    },
    onError: onQueryError,
  })

  const {
    mutate: rejectTimeSheet,
    isLoading: isRejecting,
    variables: rejectVariables,
  } = useMutation({
    mutationFn: ({
      timesheetIds,
      reason,
    }: {
      timesheetIds: number[]
      reason: string
    }) =>
      ewaAxiosInstance.post(`/v1/time-attendance/timesheets/reject`, {
        timesheetIds,
        reason,
      }),
    onSuccess: (_, { timesheetIds }) => {
      invalidateTimesheetAndOvertime()
      unselectTimesheets(timesheetIds)
      toast.success("Timesheet rejected successfully")
    },
    onError: onQueryError,
  })

  const {
    mutate: approveOvertime,
    isLoading: isApprovingOvertime,
    variables: approveOvertimeVariables,
  } = useMutation({
    mutationFn: ({
      overtimeId,
      reason,
    }: {
      overtimeId: number
      reason: string
    }) =>
      ewaAxiosInstance.post(
        `/v1/time-attendance/overtime/${overtimeId}/approve`,
        { reason }
      ),
    onSuccess: () => {
      invalidateTimesheetAndOvertime()
      toast.success("Overtime approved successfully")
    },
    onError: onQueryError,
  })

  const {
    mutate: rejectOvertime,
    isLoading: isRejectingOvertime,
    variables: rejectOvertimeVariables,
  } = useMutation({
    mutationFn: ({
      overtimeId,
      reason,
    }: {
      overtimeId: number
      reason: string
    }) =>
      ewaAxiosInstance.post(
        `/v1/time-attendance/overtime/${overtimeId}/reject`,
        { reason }
      ),
    onSuccess: () => {
      invalidateTimesheetAndOvertime()
      toast.success("Overtime rejected successfully")
    },
    onError: onQueryError,
  })

  // ─── Row selection ────────────────────────────────────────────────────────

  const handleTimeSheetRow = (id: number) =>
    setSelectedRow((prev) => ({
      ...prev,
      selectedTimesheetRow: toggleIds(prev.selectedTimesheetRow, [id]),
    }))

  const handleTimeSheetRowAll = (employees: { id: number }[]) =>
    setSelectedRow((prev) => ({
      ...prev,
      selectedTimesheetRow: toggleIds(
        prev.selectedTimesheetRow,
        // The API sends ids as strings ("274"); selection is kept as numbers.
        employees.map((e) => Number(e.id))
      ),
    }))

  // ─── Derived data ─────────────────────────────────────────────────────────

  const timesheetPendingCount = useMemo(() => {
    return timesheetRows.filter((t) => t.approvalStatus === "pending").length
  }, [timesheetRows])

  const overtimePendingCount = useMemo(() => {
    return (
      overtime?.data?.docs
        ?.flatMap((dept) => dept.records ?? [])
        .filter((r) => r.status === "pending").length ?? 0
    )
  }, [overtime?.data?.docs])

  // Keep a row if it matches the All / Pending / Rejected tab.
  // Rejected rows come back from the API as "declined".
  const keepRow = (row: ITimeSheetDocsEntity) => {
    if (tableConfig.selectedFilter === "all") return true
    if (tableConfig.selectedFilter === "rejected")
      return row.approvalStatus === "declined"
    return row.approvalStatus === tableConfig.selectedFilter
  }

  // Order rows by when they were created, following the sort dropdown.
  const byCreatedDate = (a: ITimeSheetDocsEntity, b: ITimeSheetDocsEntity) => {
    const diff =
      new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime()
    return tableConfig.sort === "new" ? -diff : diff
  }

  // What the table shows: one group per day / week / month (labelled e.g.
  // "Oct 13 - Oct 19 | 2026"), with the tab filter and sort applied. Groups
  // left empty are dropped.
  const dateGroups = toDateGroups(
    timesheet?.data?.docs ?? {},
    groupBy,
    // Group order follows the sort dropdown too.
    tableConfig.sort === "new"
  )
    .map((group) => ({
      ...group,
      employees: group.employees.filter(keepRow).sort(byCreatedDate),
    }))
    .filter((group) => group.employees.length)

  // The same rows grouped by department, for the filter drawer's department
  // list and the CSV export.
  const timesheetByDepartment = groupByDepartmentNoOvertime(
    dateGroups.flatMap((group) => group.employees),
    true
  )

  const timeSheetDataWithOverTime = useMemo(() => {
    const docs = overtime?.data?.docs ?? []
    const filtered =
      tableConfig.selectedFilter === "all"
        ? docs
        : docs.flatMap((dept) => {
            const records = dept.records?.filter(
              (r) => r.status === tableConfig.selectedFilter
            )
            return records?.length ? [{ ...dept, records }] : []
          })
    const sorted = filtered.map((dept) => ({
      ...dept,
      records: [...(dept.records ?? [])].sort((a, b) => {
        const diff =
          new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime()
        return tableConfig.sort === "new" ? -diff : diff
      }),
    }))
    return groupByDepartmentWithOvertime(sorted, true)
  }, [overtime?.data?.docs, tableConfig.selectedFilter, tableConfig.sort])

  // ─── Profile drawer ───────────────────────────────────────────────────────

  const onSelectedTimesheet = (id: number) => {
    const found = timesheetRows.find((e) => e.id === id)
    setSelectedTimesheet(found)
    setModal((prev) => ({ ...prev, viewUserProfileDrawer: true }))
  }

  const overtimeRecord = selectedTimesheet as IOvertimeRecordsEntity

  const approvingTimesheetIds = useMemo(() => {
    if (!isApproving) return new Set<number>()
    return new Set(
      (approveVariables?.timesheetIds ?? []).map((id) => Number(id))
    )
  }, [approveVariables?.timesheetIds, isApproving])

  const rejectingTimesheetIds = useMemo(() => {
    if (!isRejecting) return new Set<number>()
    return new Set(
      (rejectVariables?.timesheetIds ?? []).map((id) => Number(id))
    )
  }, [rejectVariables?.timesheetIds, isRejecting])

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      <ShiftSwapBanner
        setIsTurned={onClick}
        shiftSwapDetails={shiftSwapData}
        isLoading={isShiftSwapDataLoading}
      />

      <AttendanceRecordToolbar
        selectedFilter={
          tableConfig.selectedFilter as FilterValueAttendantRecord
        }
        onFilterChange={(e) =>
          setTableConfig((prev) => ({ ...prev, selectedFilter: e }))
        }
        sortValue={tableConfig.sort}
        onSortChange={(value) =>
          setTableConfig((prev) => ({ ...prev, sort: value }))
        }
        onOpenFilter={() => setModal((prev) => ({ ...prev, openFilter: true }))}
        timesheetPendingCount={timesheetPendingCount}
        overtimePendingCount={overtimePendingCount}
        sortIcon={sortIcon}
        filterIcon={filterIcon}
        approveTimeSheet={approveTimeSheet}
        isApproving={isApproving}
        isRejecting={isRejecting}
        rejectTimeSheet={rejectTimeSheet}
        selectedRow={selectedRow.selectedTimesheetRow}
        setActiveTab={(tab) => {
          // A new tab starts from the defaults: All, no drawer filters, page 1.
          setTableConfig((prev) => ({
            ...prev,
            activeTab: tab,
            selectedFilter: "all",
            filterQuery: "",
          }))
          filterForm.resetFields()
          setPage((prev) => ({ ...prev, currentPage: 1 }))
        }}
        activeTab={tableConfig.activeTab}
        timesheetDataDoc={timesheetByDepartment}
      />

      {/* Today's date and the day / week / month grouping */}
      <div className="flex justify-between items-center px-2 mb-2 mt-3">
        <span className="text-sm text-slate-900">{todaysDate}</span>
        {tableConfig.activeTab === "1" && (
          <div className="relative">
            <Select
              value={groupBy}
              onChange={(value) => {
                setGroupBy(value)
                setActiveCollapseKeys(undefined)
              }}
              suffixIcon={null}
              popupMatchSelectWidth={false}
              bordered={false}
              options={[
                { value: "day", label: "Group in days" },
                { value: "week", label: "Group in weeks" },
                { value: "month", label: "Group in months" },
              ]}
              className="border-2 border-slate-300 rounded-md h-10 pr-4 text-slate-900 bg-white"
            />
            <img
              className="absolute right-2 top-2.5 pointer-events-none"
              src={downIcon}
              width={20}
              height={20}
            />
          </div>
        )}
      </div>

      {/* Timesheet table */}
      {isLoadingTimesheet ? (
        <TimesheetSkeleton />
      ) : tableConfig.activeTab === "1" ? (
        dateGroups.length <= 0 ? (
          <CustomEmptyState
            imageUrl="https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/31fe2dc3-b29c-4fc6-800e-332aa225ee1f.png"
            onClick={() => {
              navigate("/dashboard/time-and-attendance/upload-timesheet")
            }}
            title="Upload Timesheet"
          />
        ) : (
          <TimeSheetTable<ITimeSheetDocsEntity>
            headers={TIMESHEET_HEADERS}
            departments={dateGroups}
            getEmployeeId={(e) => Number(e.id)}
            // Newest group open by default, until the user opens or closes one.
            activeCollapseKeys={
              activeCollapseKeys ?? String(dateGroups[0]?.department.id)
            }
            isLoading={isLoadingTimesheet}
            emptyMessage="No Employee found in Time sheet"
            selectedRow={selectedRow.selectedTimesheetRow}
            onCollapseChange={setActiveCollapseKeys}
            onSelectAll={handleTimeSheetRowAll}
            renderRow={(e) => (
              <TimeSheetActivityRow
                timesheet={e}
                key={e.id}
                id={e.id}
                name={e.employee.fullName}
                clockIn={formatTime(e.clockIn)}
                clockOut={formatTime(e.clockOut)}
                methodLabel={e.method}
                flags={buildFlags(e)}
                reason={e.reason || ""}
                status={e.approvalStatusId}
                selectRow={(id) => handleTimeSheetRow(Number(id))}
                isSelected={selectedRow.selectedTimesheetRow.includes(
                  Number(e.id)
                )}
                viewUserProfile={() => onSelectedTimesheet(e.id)}
                showModalReason={() => openModal("reason", e)}
                acceptShiftPeriodModal={() => openModal("approve", e)}
                rejectShiftPeriodModal={() => openModal("reject", e)}
                rejectDetailsModal={() => openModal("rejectDetails", e as any)}
                isLoading={
                  approvingTimesheetIds.has(Number(e.id)) ||
                  rejectingTimesheetIds.has(Number(e.id))
                }
              />
            )}
          />
        )
      ) : timeSheetDataWithOverTime.length <= 0 ? (
        <CustomEmptyState
          imageUrl="https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/31fe2dc3-b29c-4fc6-800e-332aa225ee1f.png"
          onClick={() => {
            navigate("/dashboard/time-and-attendance/upload-timesheet")
          }}
          title="Upload Timesheet"
        />
      ) : (
        <TimeSheetTable<IOvertimeRecordsEntity>
          headers={OVERTIME_HEADERS}
          departments={timeSheetDataWithOverTime}
          getEmployeeId={(e) => e.id}
          activeCollapseKeys={activeCollapseKeys}
          isLoading={isLoadingOvertime}
          emptyMessage="No Employee found in Time sheet"
          selectedRow={selectedRow.selectedTimesheetRow}
          onCollapseChange={setActiveCollapseKeys}
          onSelectAll={handleTimeSheetRowAll}
          renderRow={(e) => (
            <OvertimeTimeSheetActivityRow
              key={e.id}
              id={e.id}
              name={e.employee.fullName}
              clockIn={formatDateReadable(e.overtimeStartTime)}
              clockOut={formatDateReadable(e.overtimeEndTime)}
              flags={[]}
              reason={e.reason || ""}
              status={e.approvalStatusId}
              selectRow={(id) => handleTimeSheetRow(Number(id))}
              isSelected={selectedRow.selectedTimesheetRow.includes(e.id)}
              viewUserProfile={() => onSelectedTimesheet(e.id)}
              showModalReason={() => openModal("reason", e as any)}
              acceptShiftPeriodModal={() => openModal("overtime", e as any)}
              rejectShiftPeriodModal={() =>
                openModal("rejectOvertime", e as any)
              }
              rejectDetailsModal={() => openModal("rejectDetails", e as any)}
              isLoading={
                (isApprovingOvertime &&
                  Number(approveOvertimeVariables?.overtimeId) ===
                    Number(e.id)) ||
                (isRejectingOvertime &&
                  Number(rejectOvertimeVariables?.overtimeId) === Number(e.id))
              }
            />
          )}
        />
      )}

      {/* Pagination */}
      <div className="flex justify-start mt-6 mb-3">
        <Pagination
          current={page.currentPage}
          pageSize={page.pageSize}
          total={
            (tableConfig.activeTab === "1"
              ? timesheet?.data?.totalDocs
              : overtime?.data?.totalDocs) || 0
          }
          showSizeChanger
          pageSizeOptions={[5, 10, 20, 50]}
          onChange={(page, pageSize) => {
            setPage({ currentPage: page, pageSize })
            // Open the first group of the new page.
            setActiveCollapseKeys(undefined)
          }}
        />
      </div>

      {/* ── Timesheet detail drawer ── */}
      <TimesheetDetailDrawer
        open={modal.viewUserProfileDrawer}
        timesheetReviewDetails={viewTimesheetDetails}
        onClose={() => closeModal("viewUserProfileDrawer")}
        timesheet={selectedTimesheet as ITimeSheetDocsEntity | undefined}
        onApprove={(id) => approveTimeSheet({ timesheetIds: [id] })}
        onReject={(id) => rejectTimeSheet({ timesheetIds: [id], reason: "" })}
        isApproving={isApproving}
        isRejecting={isRejecting}
      />

      {/* ── Filter drawer ── */}
      <FilterDrawer
        open={modal.openFilter}
        onClose={() => closeModal("openFilter")}
        onApply={(queryString) => {
          setTableConfig((prev) => ({ ...prev, filterQuery: queryString }))
          setPage((prev) => ({ ...prev, currentPage: 1 }))
        }}
        workplaceId={x}
        companyId={user?.userCompanyId}
        dutyLocationId={dutyLocationId}
        departmentId={departmentId}
        form={filterForm}
        isLoading={isLoadingTimesheet}
        onReset={() => setTableConfig((prev) => ({ ...prev, filterQuery: "" }))}
        filterQuery={tableConfig?.filterQuery}
      />

      {/* ── Timesheet approve / reject / reason modal ── */}
      <CustomModal
        rootClassName="ta-scope"
        width={536}
        open={modal.reason || modal.reject || modal.approve}
        title={
          modal.reject
            ? `Reject entry for ${selectedTimesheet?.employee?.fullName}`
            : `Reason from ${selectedTimesheet?.employee?.fullName}`
        }
        onCancel={() => {
          closeModal("reject", "approve", "reason")
          rejectForm.resetFields()
        }}
        footer={
          modal.reason ? (
            <Button
              style={{ width: "100%", height: "40px" }}
              onClick={() => closeModal("reason")}
            >
              Close
            </Button>
          ) : modal.reject ? (
            <CustomButton
              color="danger"
              style={{ width: "100%", height: "40px" }}
              submitting={isRejecting}
              onClick={() => rejectForm.submit()}
            >
              Reject entry
            </CustomButton>
          ) : (
            <CustomButton
              color="primary"
              style={{ width: "100%", height: "40px" }}
              submitting={isApproving}
              onClick={() => {
                approveTimeSheet({
                  timesheetIds: [Number(selectedTimesheet?.id)],
                })
                closeModal("approve")
                rejectForm.resetFields()
              }}
              className="whitespace-nowrap"
            >
              Accept reason and approve entry
            </CustomButton>
          )
        }
      >
        <Form
          form={rejectForm}
          onFinish={(values) => {
            rejectTimeSheet({
              timesheetIds: [Number(selectedTimesheet?.id)],
              reason: values?.reason || "",
            })
            closeModal("reject")
            rejectForm.resetFields()
          }}
        >
          {modal.reason || !modal.reject ? (
            <p>{selectedTimesheet?.reason || "No reason recorded"}</p>
          ) : (
            <>
              <p>Are you sure you want to reject this timesheet entry?</p>
              <p className="font-semibold my-2">Rejection reason [optional]</p>
              <Form.Item name="reason">
                <Input.TextArea
                  placeholder="Reason"
                  style={{ width: "100%", height: "100px", resize: "none" }}
                  className=""
                />
              </Form.Item>
            </>
          )}
        </Form>
      </CustomModal>

      {/* ── Reject details modal ── */}
      <CustomModal
        rootClassName="ta-scope"
        width={600}
        open={modal.rejectDetails}
        title="Rejection details"
        onCancel={() => closeModal("rejectDetails")}
        footer={
          <CustomButton
            style={{ width: "100%", height: "40px" }}
            className="border border-gray-400"
            onClick={() => closeModal("rejectDetails")}
          >
            Close
          </CustomButton>
        }
      >
        <p className="text-sm my-2">{selectedTimesheet?.note}</p>
        <p className="text-sm my-2">
          Rejected by: <strong>{overtimeRecord?.updatedByName}</strong> on{" "}
          <strong>
            {overtimeRecord?.reviewDate
              ? formatDateReadable(overtimeRecord?.reviewDate ?? "")
              : "—"}
          </strong>
        </p>
      </CustomModal>

      {/* ── Overtime approve / reject modal ── */}
      <CustomModal
        rootClassName="ta-scope"
        width={600}
        open={modal.overtime || modal.rejectOvertime}
        title={
          modal.overtime
            ? "Overtime entry - Attention required"
            : "Reject overtime entry"
        }
        onCancel={() => closeModal("overtime", "rejectOvertime")}
        footer={
          modal.overtime ? (
            <div className="flex items-center gap-3">
              <CustomButton
                style={{ width: "100%", height: "40px" }}
                className="border border-gray-400"
                onClick={() => closeModal("overtime", "rejectOvertime")}
              >
                Approve only shift period
              </CustomButton>
              <CustomButton
                color="primary"
                style={{ width: "100%", height: "40px" }}
                submitting={
                  isApprovingOvertime &&
                  approveOvertimeVariables?.overtimeId === overtimeRecord.id
                }
                onClick={() => {
                  approveOvertime({
                    reason: selectedTimesheet?.reason || "",
                    overtimeId: overtimeRecord.id,
                  })
                  closeModal("overtime", "rejectOvertime")
                }}
                className="whitespace-nowrap"
              >
                Approve shift period and overtime
              </CustomButton>
            </div>
          ) : (
            <CustomButton
              color="danger"
              style={{ width: "100%", height: "40px" }}
              submitting={
                isRejectingOvertime &&
                rejectOvertimeVariables?.overtimeId === overtimeRecord.id
              }
              onClick={() => overtimeRejectForm.submit()}
            >
              Reject entry
            </CustomButton>
          )
        }
      >
        <h5 className="font-semibold">
          Reason for {selectedTimesheet?.employee?.fullName}
        </h5>
        {modal.overtime ? (
          <p className="my-2">{selectedTimesheet?.reason}</p>
        ) : (
          <Form
            form={overtimeRejectForm}
            onFinish={(values) => {
              rejectOvertime({
                reason: values.reason || "",
                overtimeId: overtimeRecord.id,
              })
              closeModal("overtime", "rejectOvertime")
            }}
          >
            <Form.Item name="reason">
              <Input.TextArea
                placeholder="Reason"
                style={{ width: "100%", height: "100px", resize: "none" }}
                className=""
              />
            </Form.Item>
          </Form>
        )}
        <OvertimeWarningBanner
          overtimeRecord={overtimeRecord}
          isApproving={modal.overtime}
          warningIcon={warningIcon}
        />
      </CustomModal>
    </>
  )
}

export default TimesheetContent
